import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import api from '../../utils/api';

const PrintCashPayment = () => {
    const { id } = useParams();
    const [transaction, setTransaction] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchTransaction = async () => {
            try {
                const response = await api.get(`/bank-transactions/${id}`);
                setTransaction(response.data.data || response.data);
                
                // Allow time for images to load before print
                setTimeout(() => {
                    window.print();
                }, 800);
            } catch (error) {
                console.error("Error fetching transaction", error);
            } finally {
                setLoading(false);
            }
        };

        if (id) {
            fetchTransaction();
        }
    }, [id]);

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('fr-MA', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount || 0);
    };

    if (loading) return <div className="text-center p-10" dir="rtl">جاري تحميل البيانات...</div>;
    if (!transaction) return <div className="text-center p-10" dir="rtl">حدث خطأ أو لم يتم العثور على العملية</div>;

    const mrName = transaction.expenses?.[0]?.supplier?.name || transaction.owner_name || '';
    const dateFormatted = transaction.transaction_date ? transaction.transaction_date.split('-').reverse().join('/') : '..../..../........';
    const txNumber = transaction.transaction_number || `#${transaction.id}`;
    const totalAmount = transaction.total_amount;

    // A simple function to format amount with "Dirhams"
    const amountInWords = `${formatCurrency(totalAmount)} Dirhams`;

    const numberToArabic = (n) => {
        if (n === 0) return "صفر";
        const ones = ["", "واحد", "اثنان", "ثلاثة", "أربعة", "خمسة", "ستة", "سبعة", "ثمانية", "تسعة"];
        const tens = ["", "عشرة", "عشرون", "ثلاثون", "أربعون", "خمسون", "ستون", "سبعون", "ثمانون", "تسعون"];
        const hundreds = ["", "مئة", "مئتان", "ثلاث مئة", "أربع مئة", "خمس مئة", "ست مئة", "سبع مئة", "ثمان مئة", "تسع مئة"];
        
        const parse1to99 = (num) => {
            if (num === 0) return "";
            if (num < 10) return ones[num];
            if (num === 10) return "عشرة";
            if (num === 11) return "أحد عشر";
            if (num === 12) return "اثنا عشر";
            if (num < 20) return ones[num % 10] + " عشر";
            const one = num % 10;
            const ten = Math.floor(num / 10);
            if (one === 0) return tens[ten];
            return ones[one] + " و" + tens[ten];
        };

        const parse1to999 = (num) => {
            if (num === 0) return "";
            const h = Math.floor(num / 100);
            const rem = num % 100;
            const strH = hundreds[h];
            const strRem = parse1to99(rem);
            if (strH && strRem) return strH + " و" + strRem;
            return strH || strRem;
        };

        let result = "";
        const m = Math.floor(n / 1000000);
        const th = Math.floor((n % 1000000) / 1000);
        const rem = n % 1000;

        if (m > 0) {
            if (m === 1) result += "مليون ";
            else if (m === 2) result += "مليونان ";
            else if (m >= 3 && m <= 10) result += parse1to99(m) + " ملايين ";
            else result += parse1to99(m) + " مليون ";
        }
        
        if (th > 0) {
            if (result) result += " و";
            if (th === 1) result += "ألف ";
            else if (th === 2) result += "ألفان ";
            else if (th >= 3 && th <= 10) result += parse1to99(th) + " آلاف ";
            else result += parse1to999(th) + " ألف ";
        }
        
        if (rem > 0) {
            if (result) result += " و";
            result += parse1to999(rem);
        }
        
        return result.trim();
    };

    const numberToArabicDirhams = (amount) => {
        const num = parseFloat(amount) || 0;
        const dirhams = Math.floor(num);
        const centimes = Math.round((num - dirhams) * 100);
        let text = numberToArabic(dirhams) + " درهم";
        if (centimes > 0) {
            text += " و " + numberToArabic(centimes) + " سنتيم";
        }
        return text;
    };

    const amountInArabic = numberToArabicDirhams(totalAmount);

    const ReceiptCopy = () => (
        <>
            <div className="header">
                <div className="header-left">
                    <img src="/logo.png" alt="Logo" className="logo" />
                    <h2 className="assoc-name">جمعية تنمية النقل المدرسي</h2>
                    <h3 className="assoc-subname">أولاد دحو</h3>
                </div>
                <div className="header-right">
                    <div className="date-field">
                        {dateFormatted}
                    </div>
                    <h1 className="doc-title">BON DE DEPENSES</h1>
                    <h2 className="doc-number">N° {txNumber}</h2>
                </div>
            </div>

            <div className="mr-row" style={{ marginBottom: '15px' }}>
                <div style={{ marginBottom: '10px' }}>
                    <strong>Caisse : </strong> <span style={{ textTransform: 'uppercase' }}>associ scolaire ouled dahou</span>
                </div>
                <div>
                    <strong>Bénéficiaire : </strong> <span className="dotted-line-text">{mrName}</span>
                </div>
            </div>

            <div className="content-area">
                <div className="nature-section">
                    <h3 className="section-title">NATURE</h3>
                    <div className="lines-container">
                        {transaction.expenses && transaction.expenses.length > 0 ? (
                            transaction.expenses.map((exp, idx) => (
                                <div key={idx} className="data-line">
                                    {exp.expense_type?.name || 'Dépense'}
                                </div>
                            ))
                        ) : (
                            <>
                                <div className="empty-line"></div>
                                <div className="empty-line"></div>
                            </>
                        )}
                    </div>
                </div>
                
                <div className="amount-section">
                    <div className="amount-box">
                        <div className="amount-lines">
                            {transaction.expenses && transaction.expenses.length > 0 ? (
                                transaction.expenses.map((exp, idx) => (
                                    <div key={idx} className="data-amount">
                                        {formatCurrency(exp.amount)}
                                    </div>
                                ))
                            ) : (
                                <>
                                    <div className="empty-line"></div>
                                    <div className="empty-line"></div>
                                </>
                            )}
                        </div>
                        <div className="amount-total">
                            {formatCurrency(totalAmount)}
                        </div>
                    </div>
                </div>
            </div>

            <div className="montant-lettre">
                <strong>MONTANT EN LETTRE</strong>
                <div className="lettre-lines">
                    <div className="lettre-text arabic-amount" dir="rtl">{amountInArabic}</div>
                </div>
            </div>

            <div className="signatures-box" style={{ position: 'relative' }}>
                <div style={{ position: 'absolute', top: '50%', left: '20px', transform: 'translateY(-50%)', opacity: 0.15, fontSize: '24px', zIndex: 0, whiteSpace: 'nowrap' }}>جمعية تنمية النقل المدرسي</div>
                <div style={{ position: 'absolute', top: '50%', right: '20px', transform: 'translateY(-50%)', opacity: 0.15, fontSize: '24px', zIndex: 0, whiteSpace: 'nowrap' }}>{mrName}</div>
                <div className="sig-left" style={{ zIndex: 1, position: 'relative' }}>Caissier</div>
                <div className="sig-right" style={{ zIndex: 1, position: 'relative' }}>Bénéficiaire</div>
            </div>
        </>
    );

    return (
        <div className="page-wrapper">
            <div className="print-container" style={{ borderBottom: '1px dashed #aaa' }}>
                <ReceiptCopy />
            </div>
            <div className="print-container">
                <ReceiptCopy />
            </div>

            <style dangerouslySetInnerHTML={{__html: `
                @import url('https://fonts.googleapis.com/css2?family=Cairo:wght@400;700&display=swap');
                
                body {
                    margin: 0;
                    padding: 0;
                    background: white;
                }

                .page-wrapper {
                    width: 210mm;
                    margin: 0 auto;
                }

                .print-container {
                    width: 210mm;
                    height: 148.5mm;
                    padding: 10mm 15mm;
                    margin: 0 auto;
                    font-family: Arial, sans-serif;
                    box-sizing: border-box;
                    color: #000;
                    direction: ltr;
                    overflow: hidden;
                }

                .header {
                    display: flex;
                    justify-content: space-between;
                    margin-bottom: 10px;
                }

                .header-left {
                    text-align: center;
                    font-family: 'Cairo', Arial, sans-serif;
                }

                .logo {
                    width: 70px;
                    height: 70px;
                    object-fit: contain;
                    display: block;
                    margin: 0 auto 5px auto;
                }

                .assoc-name {
                    margin: 0;
                    font-size: 16px;
                    font-weight: bold;
                }

                .assoc-subname {
                    margin: 2px 0 0;
                    font-size: 14px;
                    color: #333;
                }

                .header-right {
                    text-align: center;
                    padding-top: 10px;
                }

                .date-field {
                    font-size: 14px;
                    margin-bottom: 10px;
                    text-align: right;
                }

                .doc-title {
                    margin: 0;
                    font-size: 24px;
                    font-weight: bold;
                    letter-spacing: 1px;
                }

                .doc-number {
                    margin: 5px 0 0;
                    font-size: 18px;
                    font-weight: bold;
                }

                .mr-row {
                    margin-bottom: 10px;
                    font-size: 16px;
                }

                .dotted-line-text {
                    display: inline-block;
                    width: 300px;
                    border-bottom: 1px dotted #000;
                    padding-bottom: 2px;
                }

                .content-area {
                    display: flex;
                    margin-bottom: 10px;
                    align-items: flex-start;
                }

                .nature-section {
                    flex: 1;
                    padding-right: 20px;
                }

                .section-title {
                    text-align: center;
                    font-size: 16px;
                    font-weight: bold;
                    margin-bottom: 10px;
                }

                .lines-container {
                    margin-top: 10px;
                }

                .data-line {
                    border-bottom: 1px dotted #000;
                    height: 25px;
                    line-height: 25px;
                    margin-bottom: 5px;
                    font-family: 'Cairo', Arial, sans-serif;
                }

                .empty-line {
                    border-bottom: 1px dotted #000;
                    height: 25px;
                    margin-bottom: 5px;
                }

                .amount-section {
                    width: 250px;
                    margin-top: 10px;
                }

                .amount-box {
                    border: 2px solid #000;
                    border-top: none; /* In the image, top seems open but sides are solid, wait actually it's a box with border */
                }
                
                .amount-lines {
                    border-top: 2px solid #000;
                    padding: 10px;
                    min-height: 80px;
                }

                .data-amount {
                    border-bottom: 1px dotted #000;
                    height: 25px;
                    line-height: 25px;
                    margin-bottom: 5px;
                    text-align: center;
                    font-weight: bold;
                }

                .amount-total {
                    border-top: 2px solid #000;
                    padding: 15px 10px;
                    text-align: center;
                    font-weight: bold;
                    font-size: 16px;
                }

                .montant-lettre {
                    margin-bottom: 10px;
                }

                .lettre-lines {
                    margin-top: 10px;
                    margin-left: 50px;
                }

                .lettre-text {
                    border-bottom: 1px dotted #000;
                    padding-bottom: 5px;
                    margin-bottom: 10px;
                    font-size: 16px;
                    font-weight: bold;
                    font-style: italic;
                }
                
                .arabic-amount {
                    font-family: 'Cairo', Arial, sans-serif;
                    font-style: normal;
                }

                .empty-line-full {
                    border-bottom: 1px dotted #000;
                    margin-bottom: 10px;
                }

                .signatures-box {
                    border: 2px solid #000;
                    height: 100px;
                    display: flex;
                    justify-content: space-between;
                    padding: 5px 20px;
                    font-weight: bold;
                    font-size: 16px;
                    margin-top: 5px;
                }

                @media print {
                    @page { margin: 0; size: A4 portrait; }
                    body { background: none; }
                    .page-wrapper {
                        width: 210mm;
                        margin: 0;
                    }
                    .print-container { 
                        width: 210mm;
                        height: 148.5mm;
                        padding: 10mm 15mm; 
                        margin: 0;
                        box-shadow: none;
                    }
                }
            `}} />
        </div>
    );
};

export default PrintCashPayment;
